import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { PartnerEmail, PartnerFullForm } from './partner.component';
import { HolderService, HOLDER_STATUS_VERIFY_EMAIL, HOLDER_STATUS_NOT_ENTITLED, PARTNER_NEW_EMAIL } from '../services/holder.service';
import { PartnerModel, PartnerServiceInputModel, PartnerFormModel } from '../models/partner.model';
import { HolderModel } from '../models/holder.model';
import { Alert, AlertTypes } from './alert.component'
import { Constants } from '../util/constants';
import { FullLoader } from './loader.component';
import styles from './partner-form.module.scss';
import { getHomepath, getConfigs } from '../util/cms-utils';
import { useLocation } from 'react-router-dom';
import { Modal } from './utils.component';

interface ErrorState {
  alertType: AlertTypes;
  text: string;
}

type OwnProps = {
  holderHost: string;
}

const formatPartnerForSubmission = (holder: HolderModel, partner: PartnerFormModel): PartnerServiceInputModel => {
  if (!holder.accountId || !holder.headerETag) {
    throw new Error('Please refresh the page and try again.');
  }

  const formattedPartner: PartnerServiceInputModel = {
    ...partner,
    ticketHolderAccountId: holder.accountId,
    headerETag: holder.headerETag,
    clubCode: holder.clubCode,
    venueId: holder.venueId,
  };

  return formattedPartner;
}

const initialHolder: HolderModel = {
  firstName: '',
  lastName: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'USA',
  phoneNumberPrimary: '',
  phoneNumberSecondary: '',
  dateOfBirth: ['', '', ''],
  learnMore: [],
  clubCode: 'min',
  venueId: '3312',
  ticketHolderPartnerMappings: [],
  maxNumOfPartners: 0,
  cubsSms: false,
  cubsAlertsSms: false,
};

const initialPartner: PartnerFormModel = {
  email: '',
};

const defaultPartnerForm: PartnerFormModel = {
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'USA',
  email: '',
  phoneNumber: '',
  company: '',
  partnerType: '',
};

const PartnerInfo = ({ partner, index }: { partner: PartnerModel, index: number }) => {
  const {
    firstName,
    lastName,
    email,
  } = partner;

  return (
    <li className={styles.partnerInfo}>
      <b className={styles.partnerIndex}>({index})</b>
      <div>
        <b>{firstName} {lastName}</b>
        <p>{email}</p>
      </div>
    </li>
  );
};

const PartnerForm = ({ holderHost }: OwnProps) => {
  const { authState } = useOktaAuth()
  const holderService = new HolderService(authState, holderHost);

  const [alert, setAlert] = useState<ErrorState | null>();
  const [holder, setHolder] = useState<HolderModel>(initialHolder);
  const [partner, setPartner] = useState<PartnerFormModel>(initialPartner);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const query = new URLSearchParams(useLocation().search);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalAlert, setModalAlert] = useState<ErrorState | null>(null);
  const [showExpandedForm , setShowExpandedForm] = useState<boolean>(false);
  const searchAgainRef = useRef<HTMLButtonElement>(null!);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    event.persist();
    const { target: { name, value } } = event;
    // also reset the state
    const isCountryChanged = name === 'country';
    setPartner(prevPartner => ({
      ...prevPartner,
      [name]: value,
      ...(isCountryChanged && ({ state: '' })),
    }));
  }

  useEffect(() => {
    const config = getConfigs(query.get('clubCode'));
    const getHolder = async (): Promise<void> => {
      setShowLoading(true);
      try {
        const holder = await holderService.getValidationStatus('min', '3312');
        if (!holder || !holder.accountId) {
          window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
          return;
        }
        setHolder(holder);
      } catch (_error) {
        const error = _error as Error
        if (error.message === HOLDER_STATUS_VERIFY_EMAIL) {
          // app show email verify button
          window.parent.location.href = `${getHomepath(config)}/app`;
        } else if (error.message === HOLDER_STATUS_NOT_ENTITLED) {
          window.parent.location.href = `${getHomepath(config)}/message/not-entitled`;
        }
        console.error('error getting holder on partner form', error);
      } finally {
        setShowLoading(false);
      }
    }
    getHolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Focus the button when the modal opens
    if (showModal) {
      searchAgainRef.current.focus();
    }
  }, [showModal]);

  return (
    <div className={styles.wrapper}>
      {showLoading && (
        <FullLoader />
      )}
      {showModal && (
        <Modal>
          <form
            className={styles.modalForm}
            onSubmit={async (event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              setModalAlert(null);
              setShowLoading(true);
              try {
                const newHolder = await holderService.addPartner(formatPartnerForSubmission(holder, partner), true);
                setHolder(newHolder);
                setPartner(initialPartner);
                setShowModal(false);
                setAlert({
                  alertType: 'success',
                  text: Constants.SUCCESS_ADD_PARTNER,
                });
              } catch (_err) {
                const err = _err as Error
                console.error('error adding partner with existing account', err);
                setModalAlert({ alertType: 'error', text: err.message });
              } finally {
                setShowLoading(false);
              }
            }}
          >
            <p>An existing accountholder was found.</p>
            <p>Would you like to add them as a partner or try your search again?</p>
            <div className={styles.existingWrapper}>
              <p>{partner.firstName} {partner.lastName}</p>
              <p>{partner.email}</p>
            </div>
            <div className={styles.buttonsWrapper}>
              <button className={styles.add} type='submit'>add partner</button>
              <button
                className={styles.searchAgain}
                ref={searchAgainRef}
                type='button'
                onClick={() => {
                  setModalAlert(null);
                  setPartner(initialPartner);
                  setShowModal(false);
                }
              }>search again</button>
            </div>
            {modalAlert && (<Alert {...modalAlert} />)}
          </form>
        </Modal>
      )}
      <div className={styles.spotsRemaining}>{'You have '}<b>{holder.maxNumOfPartners || 0}</b>{` partner spot${holder.maxNumOfPartners !== 1 ? 's' : ''} remaining`}</div>
      <ol className={styles.currentPartners}>
        {(holder.ticketHolderPartnerMappings || []).filter(({ isPartnerAssociation }) => isPartnerAssociation).map((partner, i) => (<PartnerInfo key={partner.partnerAccountId} partner={partner} index={i + 1} />))}
      </ol>
      {(holder.maxNumOfPartners || 0) > 0 && (
        <>
          {showExpandedForm ? (
            <PartnerFullForm
              holder={holder}
              partner={partner}
              handleInputChange={handleInputChange}
              handleFormSubmit={async (): Promise<void> => {
                setAlert(null);
                setShowLoading(true);
                try {
                  const newHolder = await holderService.addPartner(formatPartnerForSubmission(holder, partner));
                  setHolder(newHolder);
                  setShowExpandedForm(false);
                  setPartner(initialPartner);
                  setAlert({
                    alertType: 'success',
                    text: Constants.SUCCESS_ADD_PARTNER,
                  });
                } catch (_err) {
                  const err = _err as Error
                  console.error(err, 'err with adding partner');
                  setAlert({
                    alertType: 'error',
                    text: err.message,
                  });
                } finally {
                  setShowLoading(false);
                }
              }}
            />
          ) : (
            <PartnerEmail
              holder={holder}
              email={partner.email}
              handleInputChange={showModal ? undefined : handleInputChange}
              handleFormSubmit={async (): Promise<void> => {
                setAlert(null);
                setShowLoading(true);
                try {
                  if (!holder.accountId) throw new Error('Please refresh the page and try again.');
                  const { firstName, lastName, partnerAccountId } = await holderService.searchAndValidatePartnerAccountByEmail(holder.accountId, 'min', '3312', partner.email);
                  setPartner(prevPartner => ({ ...prevPartner, firstName, lastName, partnerAccountId }));
                  setShowModal(true);
                } catch (_err) {
                  const err = _err as Error
                  if (err.message === PARTNER_NEW_EMAIL) {
                    setPartner(prevPartner => ({ ...defaultPartnerForm, ...prevPartner }));
                    setShowExpandedForm(true);
                  } else {
                    console.error(err, 'err with checking partner email');
                    setAlert({
                      alertType: 'error',
                      text: err.message,
                    });
                  }
                } finally {
                  setShowLoading(false);
                }
              }}
            />
          )}
        </>
      )}
      {alert && (<Alert {...alert} />)}
    </div>
  );
};

export default PartnerForm;
